import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { ConnectivityService } from '../sync/connectivity.service';
import { AuthService } from './auth.service';
import { resolveSessionEndReason } from './resolve-session-end-reason';
import { SessionEndReason } from './session-policy';

/** Tracks idle time and re-validates the Sanctum token while the app is open. */
@Injectable({ providedIn: 'root' })
export class SessionMonitorService {
  private static readonly CHECK_INTERVAL_MS = 5 * 60 * 1000;

  private readonly auth = inject(AuthService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly router = inject(Router);

  constructor() {
    if (typeof document === 'undefined') return;

    this.bindActivityListeners();

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.auth.touchActivity());

    window.setInterval(
      () => void this.validateSession(),
      SessionMonitorService.CHECK_INTERVAL_MS,
    );
  }

  private bindActivityListeners(): void {
    const onActivity = () => this.auth.touchActivity();
    const opts: AddEventListenerOptions = { capture: true, passive: true };

    document.addEventListener('click', onActivity, opts);
    document.addEventListener('keydown', onActivity, opts);
    document.addEventListener('pointerdown', onActivity, opts);
    document.addEventListener('scroll', onActivity, opts);
  }

  private async validateSession(): Promise<void> {
    if (!this.auth.token()) return;
    // Sin red no intentamos /me: evita errores y mantiene la sesión local.
    if (this.connectivity.isOffline()) return;

    const endReason = this.localEndReason();
    if (endReason) {
      await this.auth.handleSessionEnded(endReason, { returnUrl: this.router.url });
      return;
    }

    try {
      await this.auth.refresh();
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        const reason = resolveSessionEndReason(this.auth, err);
        await this.auth.handleSessionEnded(reason, { returnUrl: this.router.url });
      }
    }
  }

  private localEndReason(): SessionEndReason | null {
    if (this.auth.isIdleExpiredLocally()) return 'idle_expired';
    if (this.auth.isTokenExpiredLocally()) return 'session_expired';
    return null;
  }
}
